/**
 * Runs an async action for each id in parallel and returns which
 * succeeded/failed, so bulk operations can report partial failures
 * instead of silently stopping at the first error.
 *
 * @param {Array<string|number>} ids
 * @param {(id: string|number) => Promise<any>} action
 * @returns {Promise<{ succeeded: Array<string|number>, failed: Array<string|number> }>}
 */
async function runBulkAction(ids, action) {
  const results = await Promise.allSettled(ids.map(id => action(id)));

  const succeeded = [];
  const failed = [];

  results.forEach((result, index) => {
    if (result.status === 'fulfilled') {
      succeeded.push(ids[index]);
    } else {
      failed.push(ids[index]);
    }
  });

  return { succeeded, failed };
}

/**
 * Builds a human readable summary for a bulk action's outcome.
 */
function describeBulkResult({ succeeded, failed }, verb, noun) {
  const parts = [];
  if (succeeded.length > 0) {
    parts.push(`${succeeded.length} ${noun}${succeeded.length > 1 ? 's' : ''} ${verb}`);
  }
  if (failed.length > 0) {
    parts.push(`${failed.length} failed`);
  }
  return parts.join(', ') || 'Nothing to do';
}

export { runBulkAction, describeBulkResult };
